<template>
    <div class="schedule-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>
              <el-icon><Calendar /></el-icon>
              إدارة الجدول الزمني الدراسي
            </h2>
            <div class="header-buttons">
              <el-button @click="goToSettings" type="info" plain>
                <el-icon><Setting /></el-icon>
                إعدادات الجدول
              </el-button>
              <el-button type="primary" @click="handlePrint" :disabled="!filters.sectionId" plain>
                <el-icon><Printer /></el-icon>
                طباعة الجدول
              </el-button>
            </div>
          </div>
        </template>
  
        <el-row :gutter="20" style="margin-bottom: 20px;">
          <el-col :span="6">
            <el-select v-model="filters.classId" placeholder="اختر الفصل" @change="handleClassChange" style="width: 100%;">
              <el-option v-for="c in classes" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-select v-model="filters.sectionId" placeholder="اختر الشعبة" @change="handleSectionChange" style="width: 100%;" :disabled="!filters.classId">
              <el-option v-for="s in sections" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </el-col>
        </el-row>
  
        <div v-if="filters.sectionId">
          <el-row :gutter="20">
            <el-col :span="6">
              <el-card shadow="inner">
                <template #header><strong>المواد المتاحة</strong></template>
                <div v-loading="assignmentsLoading" class="palette">
                  <div v-for="assignment in availableAssignments" :key="assignment.id" class="palette-item" draggable="true" @dragstart="handleDragStart($event, assignment)">
                    <div class="subject-name">{{ assignment.course_offering.subject.name }}</div>
                    <div class="teacher-name">{{ assignment.teacher.name }}</div>
                  </div>
                  <el-empty v-if="!availableAssignments.length" description="لا توجد مواد مسندة" :image-size="80" />
                </div>
              </el-card>
            </el-col>
            <el-col :span="18">
              <div v-loading="scheduleLoading">
                <el-table v-if="schedulePeriods.length > 0" :data="schedulePeriods" border style="width: 100%">
                  <el-table-column label="الفترة" width="150">
                    <template #default="{ row }">
                      <strong>{{ row.name }}</strong><br>
                      <small>{{ row.start_time.substring(0, 5) }} - {{ row.end_time.substring(0, 5) }}</small>
                    </template>
                  </el-table-column>
                  
                  <el-table-column v-for="day in weekdays" :key="day.value" :label="day.label">
                    <template #default="{ row }">
                      <div
                        class="drop-zone"
                        :class="{ 
                          'break-time': row.is_break, 
                          'is-updating': isCellUpdating(row.id, day.value),
                          'drag-over': draggedOverCell === `${row.id}-${day.value}` 
                        }"
                        @dragover.prevent="handleDragOver($event, row.is_break)"
                        @drop="handleDrop($event, row.id, day.value)"
                        @dragenter="handleDragEnter(row, day.value)"
                        @dragleave="handleDragLeave"
                      >
                        <el-icon v-if="isCellUpdating(row.id, day.value)" class="loading-spinner is-loading"><Loading /></el-icon>
                        <div v-else-if="getScheduleEntry(row.id, day.value)" class="schedule-entry">
                          <span>
                            <strong>{{ getScheduleEntry(row.id, day.value).teacher_course_assignment.course_offering.subject.name }}</strong><br>
                            <small>{{ getScheduleEntry(row.id, day.value).teacher_course_assignment.teacher.name }}</small>
                          </span>
                          <el-button circle type="danger" :icon="Delete" size="small" class="delete-btn" @click="handleDelete(getScheduleEntry(row.id, day.value))" />
                        </div>
                        <span v-else-if="!row.is_break" class="placeholder">+</span>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
                <el-alert v-else title="الخطوة الأولى: إعداد الفترات الزمنية" type="warning" :closable="false" show-icon style="padding: 20px;">
                  <p>لم يتم تعريف أي فترات زمنية. يرجى الذهاب إلى صفحة الإعدادات لإضافتها أولاً.</p>
                  <el-button @click="goToSettings" type="primary" style="margin-top: 10px;">الانتقال إلى الإعدادات</el-button>
                </el-alert>
              </div>
            </el-col>
          </el-row>
        </div>
        <el-empty v-else description="الرجاء اختيار فصل وشعبة لعرض وبناء الجدول" />
      </el-card>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage } from 'element-plus';
  import { Calendar, Printer, Delete, Setting, Loading } from '@element-plus/icons-vue';
  
  export default {
    name: 'AcademicSchedule',
    components: { Calendar, Printer, Setting, Loading },
    setup() {
      return { Delete };
    },
    data() {
      return {
        api: null,
        classes: [],
        sections: [],
        schedulePeriods: [],
        availableAssignments: [],
        assignmentsLoading: false,
        scheduleLoading: false,
        filters: { classId: null, sectionId: null },
        scheduleGrid: {},
        updatingCells: new Set(),
        draggedOverCell: null,
        weekdays: [
          { label: 'الأحد', value: 'sunday' },
          { label: 'الاثنين', value: 'monday' },
          { label: 'الثلاثاء', value: 'tuesday' },
          { label: 'الأربعاء', value: 'wednesday' },
          { label: 'الخميس', value: 'thursday' },
        ],
      };
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchInitialData();
    },
    methods: {
      async fetchInitialData() {
        try {
          const [classesRes, periodsRes] = await Promise.all([
            this.api.get('/classes?active=true'),
            this.api.get('/schedule-periods')
          ]);
          this.classes = classesRes.data;
          this.schedulePeriods = periodsRes.data.data || periodsRes.data;
        } catch (error) {
          ElMessage.error('فشل في تحميل البيانات الأساسية.');
        }
      },
      async handleClassChange() {
        this.filters.sectionId = null;
        this.scheduleGrid = {};
        this.availableAssignments = [];
        if (!this.filters.classId) return;
  
        try {
          const response = await this.api.get(`/classes/${this.filters.classId}/sections-to`);
          this.sections = response.data.data || response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الشعب.');
        }
      },
      async handleSectionChange() {
        if (!this.filters.sectionId) return;
        this.assignmentsLoading = true;
        try {
          const assignmentsRes = await this.api.get(`/teacher-course-assignments?class_id=${this.filters.classId}&section_id=${this.filters.sectionId}`);
          this.availableAssignments = assignmentsRes.data || [];
        } catch(error) {
          ElMessage.error('فشل في تحميل المواد المتاحة.');
        } finally {
          this.assignmentsLoading = false;
        }
        await this.fetchSchedule();
      },
      async fetchSchedule() {
        if (!this.filters.sectionId) return;
        this.scheduleLoading = true;
        this.scheduleGrid = {};
        try {
          const response = await this.api.get(`/class-schedules?section_id=${this.filters.sectionId}`);
          const scheduleData = response.data || [];
          const newGrid = {};
          scheduleData.forEach(entry => {
            if (!newGrid[entry.schedule_period_id]) {
              newGrid[entry.schedule_period_id] = {};
            }
            newGrid[entry.schedule_period_id][entry.day_of_week] = entry;
          });
          this.scheduleGrid = newGrid;
        } catch (error) {
          ElMessage.error('فشل في جلب الجدول الدراسي.');
        } finally {
          this.scheduleLoading = false;
        }
      },
      getScheduleEntry(periodId, day) {
        return this.scheduleGrid[periodId]?.[day] || null;
      },
      isCellUpdating(periodId, day) {
        return this.updatingCells.has(`${periodId}-${day}`);
      },
      handleDragStart(event, assignment) {
        event.dataTransfer.setData('assignment_id', assignment.id);
        event.dataTransfer.effectAllowed = 'move';
      },
      handleDragOver(event, isBreak) {
        if (!isBreak) event.preventDefault();
      },
      handleDragEnter(row, day) {
        if (!row.is_break) {
          this.draggedOverCell = `${row.id}-${day}`;
        }
      },
      handleDragLeave() {
        this.draggedOverCell = null;
      },
      async handleDrop(event, periodId, day) {
        this.draggedOverCell = null;
        const assignmentId = event.dataTransfer.getData('assignment_id');
        if (!assignmentId || this.isCellUpdating(periodId, day) || this.getScheduleEntry(periodId, day)) {
          if (this.getScheduleEntry(periodId, day)) ElMessage.warning('هذه الخانة مشغولة بالفعل.');
          return;
        }
  
        const cellKey = `${periodId}-${day}`;
        const payload = {
          teacher_course_assignment_id: assignmentId,
          schedule_period_id: periodId,
          day_of_week: day,
        };
  
        try {
          this.updatingCells.add(cellKey);
          await this.api.post('/class-schedules', payload);
          ElMessage.success('تمت إضافة الحصة بنجاح!');
          await this.fetchSchedule();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشلت عملية إضافة الحصة.');
        } finally {
          this.updatingCells.delete(cellKey);
        }
      },
      async handleDelete(scheduleEntry) {
        if (!scheduleEntry) return;
        const cellKey = `${scheduleEntry.schedule_period_id}-${scheduleEntry.day_of_week}`;
        
        try {
          this.updatingCells.add(cellKey);
          await this.api.delete(`/class-schedules/${scheduleEntry.id}`);
          ElMessage.success('تم حذف الحصة بنجاح!');
          await this.fetchSchedule();
        } catch(error) {
          ElMessage.error('فشل في حذف الحصة.');
        } finally {
          this.updatingCells.delete(cellKey);
        }
      },
      handlePrint() {
      if (!this.filters.sectionId) return;
      // بدلاً من فتح نافذة جديدة، قم بالانتقال إلى الصفحة باستخدام الراوتر
      this.$router.push({ 
        name: 'PrintableSchedule', 
        query: { section_id: this.filters.sectionId } 
      });
    },
      goToSettings() {
        this.$router.push({ name: 'ScheduleSettings' });
      }
    },
  };
  </script>
  
  <style scoped>
  .schedule-container {
    padding: 20px;
  }
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .header-buttons {
    display: flex;
    gap: 10px;
  }
  .palette {
    min-height: 400px;
    max-height: 60vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .palette-item {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 10px;
    cursor: grab;
    background-color: #f9f9f9;
    text-align: center;
    transition: box-shadow 0.2s;
  }
  .palette-item:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  .subject-name {
    font-weight: bold;
  }
  .teacher-name {
    font-size: 0.85em;
    color: #606266;
  }
  .drop-zone {
    min-height: 70px;
    border: 2px dashed #e4e7ed;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.2s, border-color 0.2s;
  }
  .drop-zone:not(.break-time):hover {
    background-color: #f5f7fa;
  }
  .placeholder {
    font-size: 24px;
    color: #c0c4cc;
  }
  .schedule-entry {
    background-color: #ecf5ff;
    border: 1px solid #b3d8ff;
    border-radius: 4px;
    padding: 5px;
    width: 100%;
    height: 100%;
    text-align: center;
    font-size: 0.9em;
    position: relative;
  }
  .delete-btn {
    position: absolute;
    top: 2px;
    left: 2px;
  }
  .break-time {
    background-color: #f4f4f5;
    border-style: solid;
    cursor: not-allowed;
  }
  .drop-zone.is-updating {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f5f7fa;
  }
  .loading-spinner {
    font-size: 24px;
    color: #409eff;
  }
  .drop-zone.drag-over {
    background-color: #ecf5ff;
    border-color: #409eff;
  }
  </style>